import React, { ReactElement, ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { TailwindTooltip } from '../TailwindTooltip';

export interface TruncatedTextProps {
	text: string;
	className?: string;
	expanded?: boolean;
	tooltipPosition?: 'left' | 'right' | 'top' | 'bottom';
	/** Tooltip content. Defaults to `text`. */
	tooltip?: ReactNode;
	/** Tailwind classes that truncate the visible text. Defaults to a single-line `truncate`. */
	truncateClassName?: string;
	/** Tailwind width classes for the tooltip panel. Defaults to `max-w-md`. */
	tooltipWidthClass?: string;
	/** Render the tooltip panel outside overflow containers. */
	portal?: boolean;
	/** Wrap the text reference, for example in a link, without adding another focus target. */
	renderReference?(reference: ReactElement): ReactElement;
}

export function TruncatedText({
	text,
	className = '',
	expanded = false,
	tooltipPosition = 'bottom',
	tooltip,
	truncateClassName = 'truncate',
	tooltipWidthClass = 'max-w-md',
	portal = false,
	renderReference,
}: TruncatedTextProps) {
	const referenceRef = useRef<HTMLSpanElement>(null);
	const [isTruncated, setIsTruncated] = useState(false);

	const updateTruncation = useCallback(() => {
		const element = referenceRef.current;
		setIsTruncated(
			element !== null && (element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight)
		);
	}, []);

	useLayoutEffect(() => {
		updateTruncation();
		window.addEventListener('resize', updateTruncation);
		const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => updateTruncation());
		if (referenceRef.current !== null) {
			resizeObserver?.observe(referenceRef.current);
		}

		return () => {
			window.removeEventListener('resize', updateTruncation);
			resizeObserver?.disconnect();
		};
	}, [text, truncateClassName, updateTruncation]);

	if (!text) {
		return null;
	}

	if (expanded) {
		return <span className={`block min-w-0 whitespace-normal break-words ${className}`}>{text}</span>;
	}

	const textReference = (
		<span
			className={`block min-w-0 ${truncateClassName} ${className} focus:outline-none`}
			ref={referenceRef}
			tabIndex={isTruncated && renderReference === undefined ? 0 : undefined}
		>
			{text}
		</span>
	);
	const reference = renderReference?.(textReference) ?? textReference;

	return (
		<TailwindTooltip
			reference={reference}
			displayClass='block min-w-0 max-w-full'
			position={tooltipPosition}
			maxWidthClass={tooltipWidthClass}
			portal={portal}
			disabled={!isTruncated}
			cursorClassName={renderReference === undefined ? 'cursor-help' : ''}
		>
			{tooltip ?? text}
		</TailwindTooltip>
	);
}
